import { Core } from "@faire/design-tokens";
import {
  localNum,
  useStrictLocalization,
} from "@faire/web/common/localization";
import { useIsSSR } from "@faire/web/common/server-side-rendering/isSSR";
import { optimizeImageUrl } from "@faire/web/common/transform-images";
import { isNotUndefined } from "@faire/web/common/typescriptUtils";
import { IconButton } from "@faire/web/slate/IconButton";
import { Close } from "@faire/web/slate/icons/foundation/Close";
import { File as FileIcon } from "@faire/web/slate/icons/foundation/File";
import { Column, Row } from "@faire/web/slate/Layout";
import {
  getSpacing,
  GrowingSpacer,
  Spacer,
} from "@faire/web/slate/spacing";
import { Typography } from "@faire/web/slate/Typography";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import { IImage } from "@faire/web-api/indigofair/data/IImage";
import React, { FC, type JSX } from "react";
import styled from "styled-components";

import { BYTES_IN_MB, DropError } from "./const";

export interface IDroppedFile {
  fileName: string;
  fileSize?: number;
  imgPreviewUrl?: string;
  icon?: JSX.Element;
  errors?: DropError[];
  isUploading?: boolean;
  localizedErrorMessage?: (error?: string) => string;
  handleRemove?: () => void;
}

const CLOSE_ICON_SIZE = "16px";
const FILE_ICON_SIZE = "24px";
const THUMBNAIL_SIZE = 48;

export const DroppedFile: FC<IDroppedFile> = ({
  fileName,
  fileSize,
  imgPreviewUrl,
  icon,
  errors,
  isUploading,
  localizedErrorMessage,
  handleRemove,
}) => {
  const isSSR = useIsSSR();
  const hasError = errors && errors.length > 0;
  const { strictLocalize } = useStrictLocalization();

  let thumbnail = (
    <>
      <Spacer width="3x" />
      <StyledFileIcon width={FILE_ICON_SIZE} height={FILE_ICON_SIZE} />
    </>
  );
  if (isNotUndefined(imgPreviewUrl)) {
    thumbnail = (
      <Thumbnail
        src={optimizeImageUrl(
          IImage.build({
            url: imgPreviewUrl,
            width: THUMBNAIL_SIZE,
            height: THUMBNAIL_SIZE,
          }),
          isSSR
        )}
        width={THUMBNAIL_SIZE}
        height={THUMBNAIL_SIZE}
      />
    );
  } else if (icon) {
    thumbnail = icon;
  }

  let fileMessage = null;
  if (hasError) {
    fileMessage = (
      <Typography color={Core.text.critical}>
        {localizedErrorMessage?.(errors[0])}
      </Typography>
    );
  } else if (isNotUndefined(fileSize)) {
    fileMessage = (
      <Typography color={Core.text.subdued}>
        <StrictLocalMsg
          defaultMessage="{fileSizeNumberWithUnit}MB"
          description={{
            text: "File size in MegaBytes",
            img: "https://cdn.faire.com/fastly/8ab6b8b770261d2fe3426a67816049de67a9cc35185497cadb4210f3b7e70efd.png",
          }}
          values={{
            fileSizeNumberWithUnit: localNum(fileSize / BYTES_IN_MB),
          }}
        />
      </Typography>
    );
  }

  return (
    <>
      <Spacer height="2x" />
      <Container $hasError={hasError}>
        {/* Preview */}
        {thumbnail}

        <Spacer width="1x" />

        {/* File Detail */}
        <Column>
          <Typography>{fileName}</Typography>
          {fileMessage}
        </Column>
        <GrowingSpacer />

        {/* State */}
        <IconButton
          aria-label={strictLocalize(
            {
              defaultMessage: "Remove attachment of file: {uploadedFileName}",
              description: {
                text: "Screenreader label for a button to remove an uploaded or currently uploading file.",
                img: "https://cdn.faire.com/fastly/6c7b2726fe0d2a02a344a2a916ade6289d31cc0cf1ce8e068cefd47b0352ad56.png",
              },
            },
            {
              uploadedFileName: fileName,
            }
          )}
          onClick={handleRemove}
          loading={isUploading}
          variant="tertiary"
        >
          <Close fontSize={CLOSE_ICON_SIZE} />
        </IconButton>
      </Container>
    </>
  );
};

const Container = styled(Row).attrs({
  align: "center",
})<{ $hasError?: boolean }>`
  width: 100%;
  height: ${getSpacing("8x")};
  background-color: ${({ $hasError }) =>
    $hasError ? Core.surface.critical : Core.surface.subdued};
`;

const StyledFileIcon = styled(FileIcon)`
  margin: ${getSpacing("1x")};
`;

const Thumbnail = styled.img`
  margin: ${getSpacing("1x")};
`;
